import { useEffect, useMemo, useState } from 'react';

import { Close, Edit, Lock } from '@mui/icons-material';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import _ from 'lodash';
import { useRecoilRefresher_UNSTABLE, useRecoilValue } from 'recoil';

import api, { fetchParamsWithPermissions, updateUserStatus } from 'utils/api';
import { useConstants } from 'utils/hooks';
import useViewportRatio from 'utils/useViewportRatio';

import { UserStateType, userListState, userState } from './state';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import moment from 'moment';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

interface UserDetailsModalProps {
  open: boolean;
  onClose: () => void;
}

const marginSize = 48;

const modalContainerStyle = {
  margin: `${marginSize}px`,
  backgroundColor: 'white'
};

const PARAMETER_TYPES = [
  'WATER_PROPERTIES',
  'WATER_CONTENTS',
  'AIR_PROPERTIES',
  'MONTHLY_CRUISE_PARAMETERS'
];

type Permission = {
  permissionId?: number | string | null;
  fromTs?: string | null;
  toTs?: string | null;
};

type ParameterBase = {
  id: string;
  parameterName: string;
  category: string;
  parameterDesc: string;
  unit: string;
  isFree: boolean;
  location: string;
  isMonthlyAvg: boolean;
  noOfDecimalPlaces: number;
  depth: string[];
};

type ParameterWithPerm = ParameterBase & {
  permission: Permission;
};

type ParamUpdateSchema = {
  parameterId: string;
  isFree: boolean;
} & Permission;

type WindowEditor =
  | { mode: 'single'; paramId: string }
  | { mode: 'bulk' }
  | null;

const tooltipTone = 'rgba(15, 23, 42, 0.95)';
const tooltipBaseProps = {
  arrow: true,
  enterDelay: 400,
  enterNextDelay: 200,
  leaveDelay: 100,
  enterTouchDelay: 600,
  leaveTouchDelay: 1500,
  placement: 'top-start',
  componentsProps: {
    tooltip: {
      sx: {
        maxWidth: 320,
        fontSize: 12,
        lineHeight: 1.4,
        backgroundColor: tooltipTone,
        color: '#fff',
        borderRadius: 1,
        px: 1.25,
        py: 0.75,
        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.25)'
      }
    },
    arrow: {
      sx: { color: tooltipTone }
    }
  }
} as const;

// Compact human-readable window text for a parameter's access chip.
const formatWindow = (fromTs?: string | null, toTs?: string | null) => {
  if (!fromTs && !toTs) return 'Unrestricted';
  const from = fromTs ? moment(Number(fromTs)).format('DD/MM/YY') : 'Any';
  const to = toTs ? moment(Number(toTs)).format('DD/MM/YY') : '∞';
  return `${from} → ${to}`;
};

type StructuredParamsList = {
  [location: string]: {
    LIVE: { [category: string]: ParameterBase[] };
    MONTHLY: { [category: string]: ParameterBase[] };
    CRUISE: { [category: string]: ParameterBase[] };
  };
};

type TabPanelProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  value: number;
  index: number;
  setDirection?: boolean;
  pt?: number;
};

const TabPanel = (props: TabPanelProps) => {
  const { children, value, setDirection, index, pt, ...other } = props;

  return (
    <div hidden={value !== index} {...other}>
      {value === index && (
        <Box
          sx={{
            p: 3,
            pt,
            display: 'flex',
            ...(setDirection && { flexDirection: 'column' })
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
};

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  open,
  onClose
}: {
  open: any;
  onClose: any;
}) => {
  const { CONSTANTS } = useConstants();

  const data = useRecoilValue(userState);
  const [user, setUser] = useState<UserStateType>(data);
  const refreshUsers = useRecoilRefresher_UNSTABLE(userListState);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [statusPending, setStatusPending] = useState(false);
  const [statusFeedback, setStatusFeedback] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);

  const [allParams, setAllParams] = useState<ParameterWithPerm[]>([]);
  const [ogParams, setOGParams] = useState<ParameterWithPerm[]>([]);
  const allowedParamsMap = useMemo(() => {
    return new Map(
      allParams
        .filter((e) => e.permission.permissionId !== null)
        .map((v) => [v.id, v])
    );
  }, [allParams]);

  const allParamsMap: Map<string, ParameterBase> = useMemo(() => {
    if (!allParams) return new Map();
    return new Map(allParams.map((v) => [v.id, v]));
  }, [allParams]);

  // Structured object shape for tabs modal
  const structuredParams: StructuredParamsList | null = useMemo(() => {
    if (!allParams || allParams.length == 0) return null;
    const locBasedParamsWPerms = _.groupBy(allParams, 'location');
    let prefilteredStructuredParamList: StructuredParamsList = {};
    for (const location in locBasedParamsWPerms) {
      prefilteredStructuredParamList[location] = {
        LIVE: _.groupBy(
          locBasedParamsWPerms[location].filter(
            (obj) =>
              obj.category !== 'MONTHLY_CRUISE_PARAMETERS' && !obj.isMonthlyAvg
          ),
          'category'
        ),
        MONTHLY: _.groupBy(
          locBasedParamsWPerms[location].filter(
            (obj) =>
              obj.category !== 'MONTHLY_CRUISE_PARAMETERS' && obj.isMonthlyAvg
          ),
          'category'
        ),
        CRUISE: _.groupBy(
          locBasedParamsWPerms[location].filter(
            (obj) => obj.category == 'MONTHLY_CRUISE_PARAMETERS'
          ),
          'category'
        )
      };
    }

    // To maintain location order in the UI
    let structuredParamList: StructuredParamsList = {};
    Object.keys(CONSTANTS.LOCATION_ACRONYM_TO_FULL_FORM_MAP ?? {}).map(
      (location: string) => {
        if (prefilteredStructuredParamList[location]) {
          structuredParamList[location] =
            prefilteredStructuredParamList[location];
        }
      }
    );
    return structuredParamList;
  }, [allParams]);

  const [editor, setEditor] = useState<WindowEditor>(null);
  const [editorAnchor, setEditorAnchor] = useState<HTMLElement | null>(null);
  const [editorFrom, setEditorFrom] = useState<number | null>(null);
  const [editorTo, setEditorTo] = useState<number | null>(null);

  // Modal selected tabs state
  const [locationValue, setLocationValue] = useState(0);
  const [divisionValue, setDivisionValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const ratio = useViewportRatio(1440);

  const anchorPosition = {
    top: 500,
    left: 400 * ratio
  };

  useEffect(() => {
    const execute = async () => {
      const params: ParameterWithPerm[] = await fetchParamsWithPermissions(
        user?.id
      );

      const normalizeParams = params.map((p) => ({
        ...p,
        permission: {
          permissionId: p.permission?.permissionId ?? null,
          fromTs: p.permission.fromTs?.toString() ?? null,
          toTs: p.permission.toTs?.toString() ?? null
        }
      }));

      setAllParams(normalizeParams);
      setOGParams(normalizeParams);
    };

    execute();
  }, []);

  async function handleSaveUser() {
    if (!user) {
      alert('Please enter Email and fetch user details');
      return;
    }
    const paramsAccess: ParamUpdateSchema[] = [];

    allParams
      .filter(
        (p) =>
          p.permission.permissionId !== null &&
          p.permission.permissionId !== undefined
      )
      .map(({ id, permission, isFree }) => {
        // A granted param's window lives entirely in its own permission.
        paramsAccess.push({
          parameterId: id,
          isFree,
          fromTs: permission.fromTs ?? null,
          toTs: permission.toTs ?? null
        });
      });

    const invalid = paramsAccess.find(
      (p) => p.fromTs && p.toTs && Number(p.fromTs) >= Number(p.toTs)
    );
    if (invalid) {
      const param = allParamsMap.get(invalid.parameterId);
      alert(
        `Start date must be before End date${
          param ? ` for "${param.parameterDesc}"` : ''
        }`
      );
      return;
    }
    try {
      await api.patch(`/admin/users/${user.id}`, {
        ...user,
        ...(paramsAccess && { parameters: paramsAccess })
      });
      alert('Saved successfully');
      setUser(null);
      onClose();
    } catch (error: any) {
      alert(error.response.data.message);
    }
  }

  async function handleStatusChange(isActive: boolean) {
    if (!user || statusPending) return;
    setStatusPending(true);
    setStatusFeedback(null);

    try {
      await updateUserStatus(user.id, isActive);
      setUser((current) => (current ? { ...current, isActive } : current));
      setConfirmDisable(false);
      refreshUsers();
      setStatusFeedback({
        severity: 'success',
        message: `User ${isActive ? 'enabled' : 'disabled'} successfully.`
      });
    } catch (error: any) {
      setConfirmDisable(false);
      setStatusFeedback({
        severity: 'error',
        message:
          error.response?.data?.message ||
          error.message ||
          'Could not update user status.'
      });
    } finally {
      setStatusPending(false);
    }
  }

  const handleRoleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const role = event.target.name;
    if (!user) return;
    if (event.target.checked) {
      const roles = new Set([...user.roles, role]);
      setUser({ ...user, roles: Array.from(roles) });
    } else {
      const rolesSet = new Set([...user.roles]);
      rolesSet.delete(role);
      setUser({ ...user, roles: Array.from(rolesSet) });
    }
  };

  const handleParamChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const id = event.target.name;
    if (!allParamsMap.has(id)) return;
    if (event.target.checked) {
      setAllParams((prevParams: ParameterWithPerm[]) => {
        const newPerms: ParameterWithPerm[] = prevParams.map((v) => {
          if (v.id != id) return v;
          return {
            ...v,
            permission: {
              permissionId: 'GRANTED',
              fromTs: null,
              toTs: null
            }
          };
        });

        return newPerms;
      });
    } else {
      setAllParams((prevParams) => {
        const newPerms: ParameterWithPerm[] = prevParams.map((v) => {
          if (v.id != id) return v;
          return {
            ...v,
            permission: {
              permissionId: null
            }
          };
        });

        return newPerms;
      });
    }
  };

  // Writes a window into a single granted param's own permission.
  const handleParamWindowChange = (
    id: string,
    patch: { fromTs: string | null; toTs: string | null }
  ) => {
    if (!allParamsMap.has(id)) return;
    setAllParams((prevParams) =>
      prevParams.map((v) =>
        v.id != id ? v : { ...v, permission: { ...v.permission, ...patch } }
      )
    );
  };

  // Applies one window to every currently-granted paid param. Free params
  // always have full access and their window is not adjustable.
  const handleBulkWindowChange = (
    fromTs: string | null,
    toTs: string | null
  ) => {
    setAllParams((prevParams) =>
      prevParams.map((v) =>
        !v.isFree &&
        v.permission.permissionId !== null &&
        v.permission.permissionId !== undefined
          ? { ...v, permission: { ...v.permission, fromTs, toTs } }
          : v
      )
    );
  };

  const openSingleEditor = (param: ParameterWithPerm, anchor: HTMLElement) => {
    setEditor({ mode: 'single', paramId: param.id });
    setEditorAnchor(anchor);
    setEditorFrom(
      param.permission.fromTs ? Number(param.permission.fromTs) : null
    );
    setEditorTo(param.permission.toTs ? Number(param.permission.toTs) : null);
  };

  const openBulkEditor = (anchor: HTMLElement) => {
    setEditor({ mode: 'bulk' });
    setEditorAnchor(anchor);
    setEditorFrom(null);
    setEditorTo(null);
  };

  const closeEditor = () => {
    setEditor(null);
    setEditorAnchor(null);
    setEditorFrom(null);
    setEditorTo(null);
  };

  // Restores the editor form to the param's window as loaded when the modal
  // opened (does not commit until Apply).
  const resetEditorToOriginal = () => {
    if (editor?.mode !== 'single') return;
    const og = ogParams.find((p) => p.id === editor.paramId);
    setEditorFrom(og?.permission.fromTs ? Number(og.permission.fromTs) : null);
    setEditorTo(og?.permission.toTs ? Number(og.permission.toTs) : null);
  };

  const applyEditor = () => {
    if (editorFrom && editorTo && editorFrom >= editorTo) {
      alert('Start date must be before End date');
      return;
    }
    const fromTs = editorFrom ? editorFrom.toString() : null;
    const toTs = editorTo ? editorTo.toString() : null;
    if (editor?.mode === 'single') {
      handleParamWindowChange(editor.paramId, { fromTs, toTs });
    } else if (editor?.mode === 'bulk') {
      handleBulkWindowChange(fromTs, toTs);
    }
    closeEditor();
  };

  const handleLocationChange = (_: React.SyntheticEvent, newValue: number) => {
    setLocationValue(newValue);
    setDivisionValue(0);
  };

  const handleDivisionChange = (_: React.SyntheticEvent, newValue: number) => {
    setDivisionValue(newValue);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setAllParams(ogParams);
    closeEditor();
  };

  const getTabsAndTabPanels = () => {
    if (!structuredParams || !allowedParamsMap.size) return null;

    return (
      <>
        <div>
          <Box
            sx={{
              mb: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'gray.200',
              backgroundColor: 'gray.50',
              maxWidth: 700
            }}
          >
            <Typography
              variant='caption'
              color='gray.800'
              sx={{ display: 'block', whiteSpace: 'normal' }}
            >
              Check a parameter to grant access. Use the global window to apply
              one range to all granted parameters, or click individual chips to
              set per-parameter windows
            </Typography>
          </Box>
          <Button
            type='button'
            variant='secondary'
            color='dark'
            size='small'
            sx={{ mb: 1 }}
            onClick={(e) => openBulkEditor(e.currentTarget)}
          >
            Set Global Window
          </Button>

          <Tabs value={locationValue} onChange={handleLocationChange}>
            {Object.keys(structuredParams).map((location: any, idx: any) => (
              <Tab
                label={CONSTANTS.LOCATION_ACRONYM_TO_FULL_FORM_MAP?.[location]}
                key={`Tab-${idx}`}
              />
            ))}
          </Tabs>

          {Object.values(structuredParams).map((sData, idx) => (
            <TabPanel
              value={locationValue}
              index={idx}
              key={`TabPanel-${idx}`}
              setDirection={true}
              // pt={0}
            >
              <Tabs value={divisionValue} onChange={handleDivisionChange}>
                {Object.keys(sData).map((divided, sIdx) => (
                  <Tab
                    label={
                      divided == 'LIVE'
                        ? 'Sensor Live'
                        : divided == 'MONTHLY'
                          ? 'Monthly Averages'
                          : 'Cruise Data'
                    }
                    key={`Tab-${idx}-${sIdx}`}
                  />
                ))}
              </Tabs>
              {Object.values(sData).map((pData, pIdx) => (
                <TabPanel
                  value={divisionValue}
                  index={pIdx}
                  key={`TabPanel-${idx}-${pIdx}`}
                >
                  {PARAMETER_TYPES.map((type, index: number) =>
                    pData[type] ? (
                      <div
                        key={`type-${idx}-${pIdx}-${index}`}
                        style={{ marginRight: 10 }}
                      >
                        <Typography
                          variant='assistive_text'
                          color='gray.700'
                          style={{
                            ...(type == 'WATER_CONTENTS' && {
                              visibility: 'hidden'
                            })
                          }}
                        >
                          {type.replace(/_/g, ' ')}
                        </Typography>
                        <div style={{ height: 16 }} />
                        {pData[type].map((param, index: number) => {
                          const paramWPerm = allowedParamsMap.get(param?.id);

                          return (
                            <div key={`param-${idx}-${pIdx}-${index}`}>
                              <FormControlLabel
                                key={param.id}
                                control={
                                  <Checkbox
                                    color='default'
                                    checked={allowedParamsMap.has(param?.id)}
                                    onChange={handleParamChange}
                                    name={param.id}
                                    disabled={param.isFree}
                                  />
                                }
                                label={
                                  param.isFree ? (
                                    <Tooltip
                                      {...tooltipBaseProps}
                                      title='Free parameter. Granted to all users. Removal of access cannot be done here'
                                    >
                                      <span>{param.parameterDesc}</span>
                                    </Tooltip>
                                  ) : (
                                    <span>{param.parameterDesc}</span>
                                  )
                                }
                              />
                              {paramWPerm && (
                                <Box sx={{ ml: 4, mb: 1 }}>
                                  <Tooltip
                                    {...tooltipBaseProps}
                                    title={
                                      paramWPerm.isFree
                                        ? 'Access window for free parameters cannot be adjusted. They are by default unrestricted.'
                                        : 'Access window for this parameter only. Click to set a start and end date, or leave Unrestricted for full access.'
                                    }
                                  >
                                    <span>
                                      <Chip
                                        size='small'
                                        icon={<Edit style={{ fontSize: 14 }} />}
                                        label={formatWindow(
                                          paramWPerm.permission.fromTs,
                                          paramWPerm.permission.toTs
                                        )}
                                        disabled={paramWPerm.isFree}
                                        onClick={
                                          paramWPerm.isFree
                                            ? undefined
                                            : (e) =>
                                                openSingleEditor(
                                                  paramWPerm,
                                                  e.currentTarget
                                                )
                                        }
                                        sx={{
                                          cursor: paramWPerm.isFree
                                            ? 'default'
                                            : 'pointer'
                                        }}
                                      />
                                    </span>
                                  </Tooltip>
                                </Box>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : null
                  )}
                </TabPanel>
              ))}
            </TabPanel>
          ))}

          <Popover
            open={Boolean(editor && editorAnchor)}
            anchorEl={editorAnchor}
            onClose={closeEditor}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Box
              sx={{
                p: 2,
                width: 320,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
              }}
            >
              <Typography variant='body_regular_strong' color='blue.900'>
                {editor?.mode === 'single'
                  ? allParamsMap.get(editor.paramId)?.parameterDesc
                  : 'All granted parameters'}
              </Typography>
              <Typography variant='caption' color='gray.700'>
                {editor?.mode === 'bulk'
                  ? 'This window will be applied to every non-free parameter this user currently has. Leave Start and/or End empty to keep access open-ended.'
                  : 'This window will be applied to only this parameter. Leave Start and/or End empty to keep access open-ended.'}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  renderInput={(props: any) => (
                    <TextField {...props} fullWidth size='small' />
                  )}
                  label='Start'
                  value={
                    editorFrom
                      ? moment(editorFrom).format('YYYY-MM-DD HH:mm:ss')
                      : null
                  }
                  inputFormat='DD-MMM-YYYY'
                  onChange={(newValue) =>
                    setEditorFrom(
                      newValue === null
                        ? null
                        : moment(newValue).startOf('day').unix() * 1000
                    )
                  }
                />
                <DatePicker
                  renderInput={(props: any) => (
                    <TextField {...props} fullWidth size='small' />
                  )}
                  label='End'
                  value={
                    editorTo
                      ? moment(editorTo).format('YYYY-MM-DD HH:mm:ss')
                      : null
                  }
                  inputFormat='DD-MMM-YYYY'
                  onChange={(newValue) =>
                    setEditorTo(
                      newValue === null
                        ? null
                        : moment(newValue).endOf('day').unix() * 1000
                    )
                  }
                />
              </LocalizationProvider>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}
              >
                {editor?.mode === 'single' && (
                  <Tooltip
                    {...tooltipBaseProps}
                    title='Restore the window from when you opened this dialog.'
                  >
                    <Button
                      type='button'
                      variant='tertiary'
                      color='dark'
                      size='small'
                      onClick={resetEditorToOriginal}
                    >
                      Reset to original
                    </Button>
                  </Tooltip>
                )}
                <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                  <Button
                    type='button'
                    variant='secondary'
                    color='dark'
                    size='small'
                    onClick={closeEditor}
                  >
                    Cancel
                  </Button>
                  <Tooltip
                    {...tooltipBaseProps}
                    title='Apply this window only. Changes are not saved until you click Update User.'
                  >
                    <Button
                      type='button'
                      variant='primary'
                      color='dark'
                      size='small'
                      onClick={applyEditor}
                    >
                      Apply
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          </Popover>

          <Box sx={{ display: 'flex' }}>
            <Tooltip {...tooltipBaseProps} title='Save changes for this user'>
              <span>
                <Button
                  type='button'
                  variant='primary'
                  color='dark'
                  sx={{ mt: 0 }}
                  onClick={handleSaveUser}
                >
                  Update User
                </Button>
              </span>
            </Tooltip>
          </Box>
        </div>
      </>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        {user ? (
          <div style={modalContainerStyle} className='user-details'>
            <Typography
              variant='h2'
              color='blue.900'
              style={{ marginBottom: 15 }}
            >
              User Details:
            </Typography>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Name: <span>{user.name}</span>
              {user.isActive === false && (
                <Chip
                  size='small'
                  color='error'
                  icon={<Lock />}
                  label='Disabled'
                />
              )}
            </div>
            <div>
              Email: <span>{user.email}</span>
            </div>
            <div>
              Roles:
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      color='default'
                      checked={user.roles.includes(CONSTANTS.ROLE.ROLE_USER)}
                      onChange={handleRoleChange}
                      name='ROLE_USER'
                      disabled
                    />
                  }
                  label='USER'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color='default'
                      checked={user.roles.includes(CONSTANTS.ROLE.ROLE_ADMIN)}
                      onChange={handleRoleChange}
                      name='ROLE_ADMIN'
                    />
                  }
                  label='ADMIN'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color='default'
                      checked={user.roles.includes(
                        CONSTANTS.ROLE.ROLE_SUB_ADMIN
                      )}
                      onChange={handleRoleChange}
                      name='ROLE_SUB_ADMIN'
                    />
                  }
                  label='SUB ADMIN'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color='default'
                      checked={user.roles.includes(
                        CONSTANTS.ROLE.ROLE_BUOY_MAINTAINER
                      )}
                      onChange={handleRoleChange}
                      name='ROLE_BUOY_MAINTAINER'
                    />
                  }
                  label='BUOY MAINTAINER'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color='default'
                      checked={user.roles.includes(
                        CONSTANTS.ROLE.ROLE_DEVELOPER_ADMIN
                      )}
                      onChange={handleRoleChange}
                      name='ROLE_DEVELOPER_ADMIN'
                    />
                  }
                  label='DEVELOPER ADMIN'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color='default'
                      checked={user.roles.includes(
                        CONSTANTS.ROLE.ROLE_DEVELOPER
                      )}
                      onChange={handleRoleChange}
                      name='ROLE_DEVELOPER'
                    />
                  }
                  label='DEVELOPER'
                />
              </div>
            </div>
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                Quantities:{' '}
                <Button
                  sx={{
                    marginLeft: '30px'
                  }}
                  variant='primary'
                  color='light'
                  onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                  }}
                >
                  Open
                </Button>
              </div>
              <Popover
                open={Boolean(anchorEl)}
                anchorReference={anchorPosition ? 'anchorPosition' : 'anchorEl'}
                anchorPosition={anchorPosition}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'right'
                }}
                sx={{
                  '& .MuiPopover-paper': {
                    boxShadow: `0px 8px 10px rgba(0, 0, 0, 0.16)`,
                    borderRadius: '16px',
                    backgroundColor: 'white.50'
                  }
                }}
              >
                <div style={{ padding: '26px 16px 16px 16px' }}>
                  <IconButton
                    size='large'
                    style={{ float: 'right' }}
                    onClick={handlePopoverClose}
                  >
                    <Close sx={{ color: 'blue.800' }} />
                  </IconButton>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      flexDirection: 'column'
                    }}
                  >
                    {getTabsAndTabPanels()}
                  </div>
                </div>
              </Popover>
            </div>
            <div
              style={{
                display: 'flex',
                margin: 20
              }}
            >
              <Button
                type='button'
                variant='secondary'
                color='dark'
                onClick={onClose}
                disabled={statusPending}
                style={{ marginRight: 15 }}
              >
                Close
              </Button>
              <Button
                type='button'
                variant='primary'
                color='dark'
                onClick={handleSaveUser}
                disabled={statusPending}
              >
                Update User
              </Button>
              <Button
                type='button'
                variant='secondary'
                color='dark'
                disabled={statusPending}
                onClick={() =>
                  user.isActive === false
                    ? handleStatusChange(true)
                    : setConfirmDisable(true)
                }
                sx={{ ml: 2 }}
              >
                {statusPending
                  ? user.isActive === false
                    ? 'Enabling...'
                    : 'Disabling...'
                  : user.isActive === false
                    ? 'Enable User'
                    : 'Disable User'}
              </Button>
            </div>
            {statusFeedback && (
              <Alert severity={statusFeedback.severity} sx={{ mx: 2, mb: 2 }}>
                {statusFeedback.message}
              </Alert>
            )}
          </div>
        ) : null}
      </Dialog>
      <Dialog
        open={confirmDisable}
        onClose={() => {
          if (!statusPending) setConfirmDisable(false);
        }}
        aria-labelledby='disable-user-title'
      >
        <DialogTitle id='disable-user-title'>Disable user?</DialogTitle>
        <DialogContent>
          Disable {user?.name} ({user?.email})? They will be signed out and
          cannot sign in until re-enabled.
        </DialogContent>
        <DialogActions>
          <Button
            type='button'
            onClick={() => setConfirmDisable(false)}
            disabled={statusPending}
          >
            Cancel
          </Button>
          <Button
            type='button'
            color='error'
            onClick={() => handleStatusChange(false)}
            disabled={statusPending}
          >
            {statusPending ? 'Disabling...' : 'Disable User'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserDetailsModal;
